import { Observable, of } from 'rxjs';

export enum FilterElementSize {
  EXTRA_SMALL,
  SMALL,
  MEDIUM,
  LARGE,
  EXTRA_LARGE,
  FULL_SIZE,
}

export function gridClassesForFilterElementSize(size: FilterElementSize): string {
  switch (size) {
    case FilterElementSize.EXTRA_SMALL:
      return 'col-6 col-lg-1';
    case FilterElementSize.SMALL:
      return 'col-6 col-lg-2';
    case FilterElementSize.LARGE:
      return 'col-12 col-lg-4';
    case FilterElementSize.EXTRA_LARGE:
      return 'col-12 col-lg-6';
    case FilterElementSize.FULL_SIZE:
      return 'col-12';
    case FilterElementSize.MEDIUM:
    default:
      return 'col-12 col-lg-3';
  }
}

export enum FilterElementType {
  BOOLEAN = 'BOOLEAN',
  DATE_RANGE = 'DATE_RANGE',
  ENUM = 'ENUM',
  TEXT_SEARCH = 'TEXT_SEARCH',
  RESULT_FIELD = 'RESULT_FIELD',
  RESULT_FIELD_MULTI = 'RESULT_FIELD_MULTI',
}

export abstract class AbstractFilterElement {
  label?: string | undefined;

  size: FilterElementSize;

  filterKey: string;

  abstract type: FilterElementType;


  /**
   * If true, the filter overrides all other active filters. This circumvents the issue with filtering
   * collection properties as described in https://nimmsta.atlassian.net/browse/NIMRND-9074
   */
  isSolo = false;

  protected constructor(
    label: string | undefined,
    filterKey: string,
    size: FilterElementSize = FilterElementSize.SMALL,
    isSolo = false,
  ) {
    this.label = label;
    this.filterKey = filterKey;
    this.size = size;
    this.isSolo = isSolo;
  }
}

export class BooleanFilterElement extends AbstractFilterElement {
  type: FilterElementType = FilterElementType.BOOLEAN;

  constructor(
    label: string | undefined,
    filterKey: string,
    size: FilterElementSize = FilterElementSize.SMALL,
    isSolo?: boolean,
  ) {
    super(label, filterKey, size, isSolo);
  }
}

export class DateRangeFilterElement extends AbstractFilterElement {
  type: FilterElementType = FilterElementType.DATE_RANGE;

  start = true;

  end = true;

  constructor(
    label: string | undefined,
    filterKey: string,
    size: FilterElementSize = FilterElementSize.LARGE,
    isSolo?: boolean,
    start?: boolean,
    end?: boolean,
  ) {
    super(label, filterKey, size, isSolo);
    this.start = start ?? true;
    this.end = end ?? true;
  }
}

export class SelectionFilterElement extends AbstractFilterElement {
  values: Record<string, string>;

  type: FilterElementType = FilterElementType.ENUM;

  multiple: boolean;

  translate: boolean;

  searchable: boolean;

  totalValues: number | undefined;

  onSearchTermChanged: (term: string | undefined) => void;

  constructor(
    label: string | undefined,
    filterKey: string,
    values: Record<string, string>,
    size: FilterElementSize = FilterElementSize.MEDIUM,
    isSolo?: boolean,
    multiple = false,
    translate = true,
    searchable = false,
    onSearchTermChanged: (term: string | undefined) => Observable<Record<string, string>> = () => of({}),
  ) {
    super(label, filterKey, size, isSolo);
    this.values = values;
    this.multiple = multiple;
    this.translate = translate;
    this.searchable = searchable;
    this.onSearchTermChanged = (term) => onSearchTermChanged(term).subscribe((result) => {
      this.values = result;
    });
  }
}

export class TextSearchFilterElement extends AbstractFilterElement {
  type: FilterElementType = FilterElementType.TEXT_SEARCH;

  constructor(
    label: string | undefined,
    filterKey: string,
    size: FilterElementSize = FilterElementSize.SMALL,
    isSolo?: boolean,
  ) {
    super(label, filterKey, size, isSolo);
  }
}

export interface ResultFieldOption {
  key: string;
  label: string;
}

export class ResultFieldFilterElement extends AbstractFilterElement {
  type: FilterElementType = FilterElementType.RESULT_FIELD;

  /**
   * Available result fields to filter by
   */
  fields: ResultFieldOption[];

  /**
   * Placeholder text for the field selector
   */
  fieldPlaceholder: string;

  /**
   * Placeholder text for the value input
   */
  valuePlaceholder: string;

  constructor(
    label: string | undefined,
    filterKey: string,
    fields: ResultFieldOption[],
    size: FilterElementSize = FilterElementSize.LARGE,
    isSolo?: boolean,
    fieldPlaceholder = 'Select field',
    valuePlaceholder = 'Enter value',
  ) {
    super(label, filterKey, size, isSolo);
    this.fields = fields;
    this.fieldPlaceholder = fieldPlaceholder;
    this.valuePlaceholder = valuePlaceholder;
  }

  /**
   * Update available fields dynamically
   */
  setFields(fields: ResultFieldOption[]): void {
    this.fields = fields;
  }
}

export class ResultFieldMultiFilterElement extends AbstractFilterElement {
  type: FilterElementType = FilterElementType.RESULT_FIELD_MULTI;

  /**
   * Available result fields to filter by
   */
  fields: ResultFieldOption[];

  /**
   * Placeholder text for the field selector
   */
  fieldPlaceholder: string;

  /**
   * Placeholder text for the value input
   */
  valuePlaceholder: string;

  constructor(
    label: string | undefined,
    filterKey: string,
    fields: ResultFieldOption[],
    size: FilterElementSize = FilterElementSize.LARGE,
    isSolo?: boolean,
    fieldPlaceholder = 'Select field',
    valuePlaceholder = 'Enter value',
  ) {
    super(label, filterKey, size, isSolo);
    this.fields = fields;
    this.fieldPlaceholder = fieldPlaceholder;
    this.valuePlaceholder = valuePlaceholder;
  }

  /**
   * Update available fields dynamically
   */
  setFields(fields: ResultFieldOption[]): void {
    this.fields = fields;
  }
}